import React, { useEffect } from "react";
import { Button } from "@chakra-ui/react";
import { royal_blue, navy_blue } from "./env";
import { dbgLog } from "../utils/appDebugFlags";

const fbLog = (...args) => dbgLog("forms-buttons", ...args);

/**
 * Shared Forms + Process form buttons used by PatientPage and EditableDocumentPage.
 *
 * When buttons are disabled or a parent has pointer-events:none, a transparent
 * absolute-positioned overlay div sits on top and captures onMouseDown for logging.
 * This is the only reliable approach: disabled <button> elements eat ALL pointer
 * events before they can reach any wrapper, and CSS pointer-events:auto on a
 * wrapper does not help once the button itself has the HTML disabled attribute.
 */
const FormsButtons = ({
  hasQualifyingForms,
  isCheckingQualifyingForms,
  onViewForms,
  onProcessForms,
  showProcessButton = true,
  size = "sm",
  fontSize = "sm",
  height = "32px",
  px = 3,
  flex,
  minW,
  isTruncated = false,
  formsLabel = "Forms",
  processLabel = "Process form",
  readOnly = false,
  debugLabel = "",
  debugPatientId = "",
  debugPatientName = "",
  debugScheduledDate = "",
}) => {
  const disabled = isCheckingQualifyingForms || !hasQualifyingForms;

  useEffect(() => {
    fbLog(`🔘 FormsButtons [${debugLabel || "?"}] state:`, {
      hasQualifyingForms,
      isCheckingQualifyingForms,
      patientId: debugPatientId || "(not provided)",
      patientName: debugPatientName || "(not provided)",
      scheduledDate: debugScheduledDate || "(not provided)",
      disabled,
    });
  }, [hasQualifyingForms, isCheckingQualifyingForms, debugLabel, debugPatientId, debugPatientName, debugScheduledDate, disabled]);

  const sharedProps = {
    size,
    fontSize,
    height,
    px,
    color: "white",
    width: "100%",
    ...(isTruncated
      ? { isTruncated: true, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
      : {}),
    isDisabled: disabled,
    opacity: hasQualifyingForms ? 1 : 0.5,
    cursor: hasQualifyingForms ? "pointer" : "not-allowed",
    _hover: { opacity: hasQualifyingForms ? 0.9 : undefined },
  };

  // Wrapper style: position:relative so the overlay can sit on top.
  // pointer-events:auto overrides any ancestor pointer-events:none (e.g.
  // EditableDocumentPage toolbar sets pointer-events:none while isLoadingDocument).
  const wrapperStyle = {
    position: "relative",
    display: "inline-flex",
    ...(flex ? { flex } : {}),
    ...(minW ? { minWidth: minW } : {}),
    pointerEvents: "auto",
  };

  // When buttons are disabled, an absolute overlay sits on top and captures
  // onMouseDown. The overlay itself is not a form element so it is never
  // disabled and always fires events regardless of the inner button state.
  const overlayStyle = {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    cursor: "not-allowed",
    background: "transparent",
    // pointer-events:auto so it receives events even if parent has pointer-events:none
    pointerEvents: "auto",
  };

  const logDisabledClick = (label) => {
    console.log(`🔘 FormsButtons [${debugLabel || "?"}] "${label}" clicked while disabled:`, {
      hasQualifyingForms,
      isCheckingQualifyingForms,
      patientId: debugPatientId || "(not provided)",
      patientName: debugPatientName || "(not provided)",
      scheduledDate: debugScheduledDate || "(not provided)",
    });
  };

  return (
    <>
      <span style={wrapperStyle}>
        <Button
          {...sharedProps}
          bg={royal_blue}
          onClick={(e) => {
            e.stopPropagation();
            if (!hasQualifyingForms) return;
            onViewForms();
          }}
          title={hasQualifyingForms ? "View submitted forms" : "No form submissions yet"}
        >
          {formsLabel}
        </Button>
        {disabled && (
          <div style={overlayStyle} onMouseDown={() => logDisabledClick(formsLabel)} />
        )}
      </span>

      {showProcessButton && !readOnly && (
        <span style={wrapperStyle}>
          <Button
            {...sharedProps}
            bg={navy_blue}
            onClick={(e) => {
              e.stopPropagation();
              if (!hasQualifyingForms) return;
              onProcessForms();
            }}
            title={hasQualifyingForms ? "Process forms with AI" : "No form submissions yet"}
          >
            {processLabel}
          </Button>
          {disabled && (
            <div style={overlayStyle} onMouseDown={() => logDisabledClick(processLabel)} />
          )}
        </span>
      )}
    </>
  );
};

export default FormsButtons;
